/* eslint-disable react/prop-types */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { InfoIcon } from '../../utils/commonIcons';

function ChartHelpTooltipTable({ rows }) {
  const cellRight = {
    verticalAlign: 'top',
    padding: '8px 10px',
    borderRadius: '0 6px 6px 0',
    background: 'rgba(148,163,184,0.1)',
    color: '#CBD5E1',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };

  return (
    <div style={{ textAlign: 'left', maxWidth: 'min(440px, calc(100vw - 24px))', boxSizing: 'border-box' }}>
      <table
        role="presentation"
        cellPadding={0}
        cellSpacing={0}
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 6px',
          margin: 0,
          fontSize: 12,
        }}
      >
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td
                style={{
                  verticalAlign: 'top',
                  padding: '8px 10px',
                  borderRadius: '6px 0 0 6px',
                  background: row.leftBg,
                  borderLeft: `3px solid ${row.borderLeft}`,
                  color: '#F8FAFC',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: '1%',
                }}
              >
                {row.label}
              </td>
              <td style={cellRight}>{row.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BURNDOWN_ROWS = [
  {
    key: 'summary',
    label: 'Summary',
    leftBg: 'rgba(59,130,246,0.16)',
    borderLeft: '#3B82F6',
    text: 'Shows how much work is left in the sprint and compares actual progress with the planned progress.',
  },
  {
    key: 'ideal',
    label: 'Ideal',
    leftBg: 'rgba(245,158,11,0.14)',
    borderLeft: '#F59E0B',
    text: 'The planned progress line. It shows how work should reduce evenly from the start to the end of the sprint.',
  },
  {
    key: 'actual',
    label: 'Actual',
    leftBg: 'rgba(6,111,209,0.16)',
    borderLeft: '#066FD1',
    text: 'The real progress line. It shows how much work is actually remaining based on completed work.',
  },
  {
    key: 'capacity',
    label: 'Capacity',
    leftBg: 'rgba(76,175,80,0.16)',
    borderLeft: '#4CAF50',
    text: "Shows the team's available working hours during the sprint.",
  },
  {
    key: 'target',
    label: 'Target',
    leftBg: 'rgba(69,126,255,0.14)',
    borderLeft: '#457EFF',
    text: 'Total work committed at the start of the sprint (Story Points or Hours).',
  },
  {
    key: 'today',
    label: "Today's",
    leftBg: 'rgba(34,197,94,0.14)',
    borderLeft: '#22C55E',
    text: 'Work completed today. Weekends and holidays are not included.',
  },
  {
    key: 'yesterday',
    label: "Yesterday's",
    leftBg: 'rgba(148,163,184,0.14)',
    borderLeft: '#94A3B8',
    text: 'Work completed yesterday. Useful for comparing daily progress.',
  },
  {
    key: 'avg',
    label: '5 Sprints Avg',
    leftBg: 'rgba(129,140,248,0.14)',
    borderLeft: '#818CF8',
    text: 'Average committed work from the last 5 completed sprints. Helps compare the current sprint with past sprints.',
  },
];

const BURNUP_ROWS = [
  {
    key: 'summary',
    label: 'Summary',
    leftBg: 'rgba(59,130,246,0.16)',
    borderLeft: '#3B82F6',
    text: 'Shows how much work has been completed compared to the total sprint work.',
  },
  {
    key: 'ideal',
    label: 'Ideal',
    leftBg: 'rgba(245,158,11,0.14)',
    borderLeft: '#F59E0B',
    text: 'The planned progress line showing how completed work should increase evenly throughout the sprint.',
  },
  {
    key: 'actualWithout',
    label: 'Without Added',
    leftBg: 'rgba(6,111,209,0.16)',
    borderLeft: '#066FD1',
    text: 'Shows completed work based only on the work originally planned at the start of the sprint.',
  },
  {
    key: 'actualWith',
    label: 'With Added',
    leftBg: 'rgba(14,165,233,0.14)',
    borderLeft: '#0EA5E9',
    text: 'Shows completed work including any new work that was added during the sprint.',
  },
  {
    key: 'addedToSprint',
    label: 'Added to Sprint',
    leftBg: 'rgba(34,197,94,0.14)',
    borderLeft: '#22C55E',
    text: 'New tickets or extra story points/hours added to the sprint after it started. Increases total sprint work.',
  },
  {
    key: 'removedFromSprint',
    label: 'Removed from Sprint',
    leftBg: 'rgba(239,68,68,0.14)',
    borderLeft: '#EF4444',
    text: 'Work moved out of the sprint to another sprint or the backlog. Reduces total sprint work.',
  },
  {
    key: 'reopened',
    label: 'Reopened',
    leftBg: 'rgba(168,85,247,0.14)',
    borderLeft: '#A855F7',
    text: 'A ticket that was marked done is opened again. Completed work goes down for that day.',
  },
  {
    key: 'calc',
    label: 'Calculation',
    leftBg: 'rgba(148,163,184,0.14)',
    borderLeft: '#94A3B8',
    text: 'Progress is calculated using completed work, reopened work, and any scope changes made during the sprint.',
  },
];

function ChartHelpTooltipIcon({ tooltipId, theme, placement, rows }) {
  const tipStyle = {
    backgroundColor: theme === 'dark' ? '#173A5A' : '#0D1621',
    borderStyle: theme === 'dark' ? 'solid' : 'none',
    borderWidth: theme === 'dark' ? '1px' : '0px',
    borderColor: theme === 'dark' ? '#224F78' : 'transparent',
    color: theme === 'dark' ? 'white' : '#FFFFFF',
    zIndex: 99999,
    padding: '8px',
    borderRadius: '5px',
    maxWidth: 'min(480px, calc(100vw - 24px))',
    whiteSpace: 'normal',
    fontSize: '12px',
    lineHeight: 1.45,
  };

  const tooltipNode = (
    <Tooltip
      id={tooltipId}
      place={placement}
      float={false}
      positionStrategy="fixed"
      arrowColor={theme === 'dark' ? '#173A5A' : '#0D1621'}
      opacity={1}
      style={tipStyle}
      render={() => <ChartHelpTooltipTable rows={rows} />}
    />
  );

  return (
    <>
      <div className="relative z-[10] shrink-0 inline-flex">
        <span
          data-tooltip-id={tooltipId}
          data-tooltip-place={placement}
          className="cursor-pointer inline-flex"
        >
          <InfoIcon className={`w-4 h-4 ${theme === 'light' ? 'text-[#5580A6]' : 'text-[#A3B1C9]'}`} />
        </span>
      </div>
      {typeof document !== 'undefined' ? createPortal(tooltipNode, document.body) : null}
    </>
  );
}

export function BurndownChartHelpTooltipIcon({ theme, placement = 'top-start' }) {
  const tooltipIdRef = useRef(`tooltip-standup-burndown-${Math.random().toString(36).slice(2, 11)}`);
  return (
    <ChartHelpTooltipIcon
      tooltipId={tooltipIdRef.current}
      theme={theme}
      placement={placement}
      rows={BURNDOWN_ROWS}
    />
  );
}

export function BurnupChartHelpTooltipIcon({ theme, placement = 'top-start' }) {
  const tooltipIdRef = useRef(`tooltip-standup-burnup-${Math.random().toString(36).slice(2, 11)}`);
  return (
    <ChartHelpTooltipIcon
      tooltipId={tooltipIdRef.current}
      theme={theme}
      placement={placement}
      rows={BURNUP_ROWS}
    />
  );
}

BurndownChartHelpTooltipIcon.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  placement: PropTypes.string,
};

BurnupChartHelpTooltipIcon.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  placement: PropTypes.string,
};
